import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import ShinyText from '../components/ShinyText';
import { 
  Play, 
  BookOpen, 
  Trophy, 
  Target, 
  Zap, 
  ArrowRight,
  Award,
  Users,
  Crown,
  Mic
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const { user, loading } = useUser();

  // Show loading only if we're actually loading and there's a token
  if (loading && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user and no token, show the home page without user stats
  const isAuthenticated = !!user;

  const features = [
    {
      icon: Mic,
      title: 'Live Debate Mode',
      description: 'Engage in real-time debates with AI opponents across multiple formats and difficulty levels.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: BookOpen,
      title: 'Training & Coaching',
      description: 'Learn debate techniques, logical fallacies, and rhetorical strategies with personalized feedback.',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Trophy,
      title: 'Tournaments',
      description: 'Compete in tournaments with real prizes, XP requirements, and crown rewards.',
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: Crown,
      title: 'Crown System',
      description: 'Earn crowns by winning 1v1 matches and climb the leaderboard to become a debate champion.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Zap,
      title: 'XP & Gamification',
      description: 'Level up through XP earned from debates, tutorials, and daily streaks.',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Join a community of debaters, share strategies, and participate in group discussions.',
      color: 'from-pink-500 to-pink-600'
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Active Debaters' },
    { number: '50+', label: 'Debate Topics' },
    { number: '5', label: 'Debate Formats' },
    { number: '1000+', label: 'Tournaments Won' }
  ];

  const debateFormats = [
    'Lincoln-Douglas',
    'Parliamentary',
    'Model Congress',
    'Model UN',
    'IPDA'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Master the Art of
                <span className="block">
                  <ShinyText text="Debate" speed={5} className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400" />
                </span>
              </h1>
              <p className="text-xl mb-8 text-gray-200 leading-relaxed">
                Join DebateRoyale and become a champion debater through AI coaching, 
                live competitions, and gamified learning. Develop critical thinking, 
                persuasive skills, and civic engagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={isAuthenticated ? "/live-debate" : "/login"}
                  className="btn-primary bg-white text-primary-700 hover:bg-gray-100 flex items-center justify-center space-x-2 text-lg px-8 py-4"
                >
                  <Play className="w-5 h-5" />
                  <span>Start Debating</span>
                </Link>
                <Link
                  to={isAuthenticated ? "/training" : "/login"}
                  className="btn-outline border-white text-white hover:bg-white hover:text-primary-700 flex items-center justify-center space-x-2 text-lg px-8 py-4"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Learn Skills</span>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              {isAuthenticated ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="text-center mb-6">
                    <Crown className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
                    <h3 className="text-2xl font-bold mb-2">Your Stats</h3>
                    <p className="text-gray-300">Track your progress</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Level</span>
                      <span className="font-bold">{user?.level}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>XP</span>
                      <span className="font-bold">{user?.xp.toLocaleString()} / {(user?.level * 1000).toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full" 
                        style={{ width: `${((user?.xp % 1000) / 1000) * 100}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Crowns</span>
                      <span className="font-bold flex items-center">
                        <Crown className="w-4 h-4 mr-1" />
                        {user?.crowns}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Streak</span>
                      <span className="font-bold flex items-center">
                        <span className="text-[10px] leading-none mr-1">🔥</span>
                        {user?.streak} days
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="text-center mb-6">
                    <Crown className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
                    <h3 className="text-2xl font-bold mb-2">Join the Community</h3>
                    <p className="text-gray-300">Start your debate journey today</p>
                  </div>
                  <div className="space-y-4">
                    <p className="text-gray-200 text-center mb-4">
                      Create an account to track your progress, earn XP, and compete on the leaderboard.
                    </p>
                    <div className="flex flex-col gap-3">
                      <Link
                        to="/register"
                        className="btn-primary bg-white text-primary-700 hover:bg-gray-100 flex items-center justify-center space-x-2 py-3"
                      >
                        <span>Sign Up</span>
                      </Link>
                      <Link
                        to="/login"
                        className="btn-outline border-white text-white hover:bg-white hover:text-primary-700 flex items-center justify-center space-x-2 py-3"
                      >
                        <span>Log In</span>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 dark:text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need to Excel
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Our comprehensive platform combines AI technology with proven debate methodologies 
              to create an engaging learning experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="card hover:shadow-xl transition-shadow duration-300"
                >
                  <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Debate Formats Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Multiple Debate Formats
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Practice different debate styles and find your preferred format
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {debateFormats.map((format, index) => (
              <div
                key={format}
                className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border border-primary-200 dark:border-primary-700 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-between">
                                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{format}</h3>
                <Target className="w-5 h-5 text-primary-600" />
              </div>
              <p className="text-gray-600 dark:text-gray-300 mt-2 text-sm">
                Master the unique rules and strategies of {format} debate format.
              </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Ready to Become a Debate Champion?
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              Join thousands of students developing critical thinking and persuasive skills
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="btn-primary text-lg px-8 py-4 flex items-center"
                onClick={() => navigate(isAuthenticated ? '/live-debate/room' : '/login')}
              >
                <Play className="w-5 h-5 mr-2" />
                Start Your First Debate
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
              <Link
                to="/leaderboard"
                className="btn-outline border-white text-white hover:bg-white hover:text-primary-700 flex items-center justify-center space-x-2 text-lg px-8 py-4"
              >
                <Award className="w-5 h-5" />
                <span>View Leaderboard</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 